import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-layout-grid-line',
    title: 'Product Organization',
    desc: 'Keep product links, prices, images, and detailed notes systematically organized in one place. A structured Superbuy Spreadsheet eliminates the chaos of scattered bookmarks and messy notes.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Price Tracking',
    desc: 'Monitor product price changes over time and compare options across different sources. Smart price tracking helps you make informed purchasing decisions and never miss a great deal.',
  },
  {
    icon: 'ri-flow-chart',
    title: 'Shopping Workflow',
    desc: 'Create a repeatable and efficient research process that saves time. A well-designed Superbuy Spreadsheet workflow turns chaotic shopping into a streamlined, stress-free experience.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Order Management',
    desc: 'Track all your purchases, shipping statuses, and important order details in one centralized system. Never lose track of an order or miss a delivery update again.',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Data Analysis',
    desc: 'Make smarter shopping decisions using organized information and historical data. Analyze spending patterns, compare product performance, and optimize your future purchases.',
  },
  {
    icon: 'ri-timer-line',
    title: 'Time Saving',
    desc: 'Reduce repetitive manual work with automated spreadsheet workflows. What used to take hours of manual searching and comparing now takes just minutes with a proper system in place.',
  },
];

export default function SeoContent() {
  return (
    <>
      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <article>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                Educational Resource
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-5">
                What Is Superbuy Spreadsheet?
              </h2>
              <div className="prose-custom space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                <p>
                  <strong>Superbuy Spreadsheet</strong> is a structured and systematic way to organize product information, shopping research data, prices, product links, order details, and complete workflow information in a single, accessible format. Rather than juggling multiple browser tabs, sticky notes, and scattered bookmarks, a <strong>Superbuy Spreadsheet</strong> brings everything together into one organized hub.
                </p>
                <p>
                  Whether you are a complete beginner just starting to explore online shopping or an experienced buyer managing dozens of orders across multiple platforms, a well-designed <strong>spreadsheet system</strong> can dramatically improve your efficiency, reduce costly mistakes, and simplify the entire product management lifecycle. The concept has gained significant traction in online communities as more users discover the power of organized data.
                </p>
                <p>
                  At its core, a <strong>Superbuy Spreadsheet template</strong> includes columns for product names, URLs, prices in multiple currencies, size and color variants, shipping estimates, agent fees, quality check notes, and final delivery status. Advanced users extend their spreadsheets with automated calculations, conditional formatting for price alerts, and integrated tracking number monitoring.
                </p>
                <p>
                  The beauty of the <strong>Superbuy Spreadsheet system</strong> lies in its flexibility &mdash; you can start with a simple Google Sheets template and gradually add complexity as your needs grow. From tracking a single product order to managing complex multi-item hauls, the spreadsheet adapts to your workflow rather than forcing you into a rigid structure.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
              Why Superbuy Spreadsheet
            </span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
              Why Thousands Use Superbuy Spreadsheet
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              Discover why a growing community of online shoppers relies on spreadsheet-based systems for smarter, more organized purchasing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/60 hover:border-accent-300/60 transition-all duration-300"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-300">
                  <i className={`${feature.icon} text-lg md:text-xl`}></i>
                </div>
                <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-start">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                  Security First
                </span>
                <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-5">
                  Is Superbuy Spreadsheet Safe?
                </h2>
                <div className="space-y-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                  <p>
                    Security is a top priority when using any <strong>Superbuy Spreadsheet</strong> system. We focus exclusively on safe online shopping practices and data organization methods. Our educational resources emphasize protecting your personal information throughout the entire shopping workflow.
                  </p>
                  <p>
                    We <strong>never</strong> request or store any of the following sensitive information:
                  </p>
                  <ul className="space-y-2 ml-5">
                    <li className="flex items-start gap-2">
                      <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
                      <span>Passwords or account credentials of any kind</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
                      <span>Payment information or credit card details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-shield-check-line text-accent-500 mt-0.5"></i>
                      <span>Personal identification documents or verification codes</span>
                    </li>
                  </ul>
                  <p>
                    Users should always verify website authenticity, protect their personal information carefully, and only use trusted and reputable sources. A <strong>Superbuy Spreadsheet</strong> is a tool for organization &mdash; it should never be a security risk when used properly with common-sense precautions.
                  </p>
                </div>
              </div>

              <div className="w-full lg:w-80 shrink-0">
                <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-5 md:p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-shield-star-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">Safety Checklist</h3>
                  <ul className="space-y-2 text-sm text-foreground-600">
                    <li className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500"></i>
                      <span>Use trusted platforms only</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500"></i>
                      <span>Verify seller authenticity</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500"></i>
                      <span>Keep software updated</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500"></i>
                      <span>Never share credentials</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-accent-500"></i>
                      <span>Use unique passwords</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                In-Depth Guide
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                Superbuy Spreadsheet: Expert Resources &amp; Guides
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                Dive deep into our comprehensive collection of guides covering everything from basic setup and free templates to advanced strategies, security best practices, and platform comparisons.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-8">
              <Link
                to="/superbuy-spreadsheet-complete-guide"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-book-open-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Complete Guide</h3>
                <p className="text-xs text-foreground-600">Everything about Superbuy Spreadsheet from basics to advanced strategies.</p>
              </Link>
              <Link
                to="/superbuy-spreadsheet-templates"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-file-copy-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Free Templates</h3>
                <p className="text-xs text-foreground-600">Download the best free Superbuy Spreadsheet templates for Google Sheets and Excel.</p>
              </Link>
              <Link
                to="/superbuy-spreadsheet-tutorial"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-secondary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-play-circle-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Beginner Tutorial</h3>
                <p className="text-xs text-foreground-600">Step-by-step tutorial to build your first Superbuy Spreadsheet in 30 minutes.</p>
              </Link>
              <Link
                to="/superbuy-spreadsheet-safety"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                  <i className="ri-shield-check-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Safety Guide</h3>
                <p className="text-xs text-foreground-600">Complete security and privacy best practices for safe spreadsheet usage.</p>
              </Link>
              <Link
                to="/superbuy-spreadsheet-comparison"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-scales-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Comparison Guide</h3>
                <p className="text-xs text-foreground-600">Superbuy Spreadsheet vs apps, bookmarks, notes, and other alternatives.</p>
              </Link>
              <Link
                to="/superbuy-spreadsheet-complete-guide"
                className="p-5 rounded-xl bg-background-100 border border-background-200/60 hover:border-secondary-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                  <i className="ri-lightbulb-line"></i>
                </div>
                <h3 className="font-heading text-sm text-foreground-900 mb-1">Advanced Tips</h3>
                <p className="text-xs text-foreground-600">Expert strategies for power users including automation and data analysis.</p>
              </Link>
            </div>

            <div className="text-center">
              <Link
                to="/superbuy-spreadsheet-complete-guide"
                className="inline-flex items-center px-7 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Read the Complete Guide
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-3">
                Frequently Asked Questions
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                Superbuy Spreadsheet FAQ
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                Quick answers to the most common questions about Superbuy Spreadsheet.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: 'What is Superbuy Spreadsheet?',
                  a: 'Superbuy Spreadsheet is a structured organizational system for managing product research, price tracking, order details, and shopping workflows. It helps users keep all their shopping information organized in one accessible spreadsheet format, reducing confusion and improving efficiency.',
                },
                {
                  q: 'Is Superbuy Spreadsheet free to use?',
                  a: 'Yes, you can create your own Superbuy Spreadsheet using free tools like Google Sheets or Microsoft Excel. Many community members share free templates that you can download and customize for your specific needs without any cost.',
                },
                {
                  q: 'How do I create a Superbuy Spreadsheet?',
                  a: 'Start with a blank spreadsheet and add columns for product name, URL, price, size, color, shipping cost, and order status. You can also download a pre-made Superbuy Spreadsheet template and customize it to match your workflow preferences.',
                },
                {
                  q: 'Is Superbuy Spreadsheet safe to use?',
                  a: 'Yes, a Superbuy Spreadsheet itself is completely safe as it is simply an organizational tool. The safety depends on how you use it. Never store passwords or payment information in your spreadsheet, and always use trusted platforms for actual purchases.',
                },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="font-heading text-sm md:text-base text-foreground-900 pr-4">{faq.q}</span>
                    <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200/50 group-open:bg-accent-100 group-open:text-accent-600 transition-all duration-300">
                      <i className="ri-add-line text-sm group-open:hidden"></i>
                      <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}